import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, TextField, Stepper, Step, StepLabel,
  Dialog, DialogContent, CircularProgress, Alert, Paper, List, ListItem,
  ListItemButton, ListItemText, ToggleButtonGroup, ToggleButton,
  Checkbox, ListItemIcon,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LanguageIcon from '@mui/icons-material/Language';
import adminService from '../services/adminService';
import authService from '../services/authService';
import { useUnion } from '../contexts/UnionContext';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'et', label: 'Eesti keel' },
  { value: 'ru', label: 'Русский' },
  { value: 'fi', label: 'Suomi' },
  { value: 'lv', label: 'Latviešu' },
  { value: 'lt', label: 'Lietuvių' },
  { value: 'uk', label: 'Українська' },
];

function Onboarding({ onComplete }) {
  const { t, i18n } = useTranslation();
  const { refreshUnions, switchUnion } = useUnion();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const STEPS = [
    t('onboarding.stepLanguage'), t('onboarding.stepCreateUnion'), t('onboarding.stepFindBuilding'),
    t('onboarding.stepImportApts'), t('onboarding.stepUtilities'), t('onboarding.stepDone'),
  ];

  // Step 0: Language
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');

  // Step 1: Create union
  const [unionName, setUnionName] = useState('');
  const [regCode, setRegCode] = useState('');
  const [createdUnion, setCreatedUnion] = useState(null);

  // Step 2: Find building
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [populateResult, setPopulateResult] = useState(null);

  // Step 3: Import apartments
  const [importedCount, setImportedCount] = useState(0);

  // Step 4: Utility types
  const [utilityFile, setUtilityFile] = useState(null);
  const [utilitySuggestions, setUtilitySuggestions] = useState([]);
  const [selectedUtilities, setSelectedUtilities] = useState([]);
  const [utilitiesCreated, setUtilitiesCreated] = useState(0);

  const handleLanguageChange = (e, val) => {
    if (!val) return;
    setSelectedLanguage(val);
    i18n.changeLanguage(val);
  };

  const handleSelectLanguage = async () => {
    setLoading(true);
    setError('');
    try {
      await authService.updateProfile({ language: selectedLanguage });
      setActiveStep(1);
    } catch {
      setError(t('onboarding.failedLanguage'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnion = async () => {
    if (!unionName.trim() || !regCode.trim()) {
      setError(t('onboarding.nameAndCodeRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const union = await adminService.createUnion({
        name: unionName.trim(),
        registration_code: regCode.trim(),
      });
      setCreatedUnion(union);
      await refreshUnions();
      await switchUnion(union.id);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.registration_code?.[0] || err.response?.data?.name?.[0] || t('onboarding.failedCreateUnion'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAddress = async () => {
    if (addressQuery.length < 3) return;
    setSearching(true);
    setError('');
    try {
      const results = await adminService.searchAddresses(addressQuery);
      const buildings = results.filter(r => r.liikNimi === 'EHITISHOONE');
      setSuggestions(buildings.length > 0 ? buildings : results);
    } catch {
      setError(t('onboarding.addressSearchFailed'));
    } finally {
      setSearching(false);
    }
  };

  const handleSelectAddress = async (suggestion) => {
    setLoading(true);
    setError('');
    const fullAddress = suggestion.taisaadress || suggestion.ipikkaadress || '';
    try {
      const result = await adminService.populateUnionFromAddress(createdUnion.id, fullAddress);
      setPopulateResult(result);
      setSuggestions([]);
      setActiveStep(3);
    } catch (err) {
      setError(t('onboarding.failedPopulate', { error: err.response?.data?.error || err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleImportApartments = async () => {
    if (!populateResult?.apartments?.length) {
      setActiveStep(4);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await adminService.importApartments(createdUnion.id, populateResult.apartments);
      setImportedCount(populateResult.apartments.length);
      setActiveStep(4);
    } catch (err) {
      setError(t('onboarding.failedImportApts', { error: err.response?.data?.error || err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadBill = async () => {
    if (!utilityFile) {
      setActiveStep(5);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await adminService.importUtilityTypesFromBill(utilityFile);
      const sug = result.suggestions || [];
      setUtilitySuggestions(sug);
      setSelectedUtilities(sug.map((_, i) => i));
    } catch {
      setError(t('onboarding.failedExtractUtilities'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUtility = (index) => {
    setSelectedUtilities((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleCreateUtilities = async () => {
    setLoading(true);
    setError('');
    let created = 0;
    try {
      for (const i of selectedUtilities) {
        const s = utilitySuggestions[i];
        await adminService.createUtilityType({
          name: s.name,
          calculation_method: s.calculation_method || 'FIXED_PER_APT',
          unit_price: s.unit_price || 0,
          meter_scope: s.meter_scope || null,
        });
        created++;
      }
      setUtilitiesCreated(created);
      setActiveStep(5);
    } catch (err) {
      setError(t('onboarding.partialUtilityFail', { count: created, error: err.response?.data?.error || err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    if (onComplete) onComplete();
  };

  return (
    <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: { minHeight: 500 } }}>
      <DialogContent>
        <Typography variant="h5" sx={{ mb: 1, textAlign: 'center' }}>
          {t('onboarding.welcome')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          {t('onboarding.subtitle')}
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Step 0: Language */}
        {activeStep === 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <LanguageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" gutterBottom>{t('onboarding.chooseLanguage')}</Typography>
            <ToggleButtonGroup
              value={selectedLanguage}
              exclusive
              onChange={handleLanguageChange}
              orientation="vertical"
              sx={{ width: '100%', mb: 3 }}
            >
              {LANGUAGES.map((l) => (
                <ToggleButton key={l.value} value={l.value} sx={{ justifyContent: 'center', py: 1.2 }}>
                  {l.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button
              variant="contained" fullWidth onClick={handleSelectLanguage}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : t('common.continue')}
            </Button>
          </Box>
        )}

        {/* Step 1: Create Union */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('onboarding.createYourUnion')}</Typography>
            <TextField
              fullWidth label={t('onboarding.unionName')} value={unionName}
              onChange={(e) => setUnionName(e.target.value)}
              sx={{ mb: 2 }} placeholder="e.g. KÜ Tamme 5"
            />
            <TextField
              fullWidth label={t('onboarding.registrationCode')} value={regCode}
              onChange={(e) => setRegCode(e.target.value)}
              sx={{ mb: 3 }} placeholder="e.g. 80123456"
            />
            <Button
              variant="contained" fullWidth onClick={handleCreateUnion}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : t('onboarding.stepCreateUnion')}
            </Button>
            <Button variant="text" fullWidth onClick={() => setActiveStep(0)} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>
        )}

        {/* Step 2: Find Building */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('onboarding.findYourBuilding')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('onboarding.searchByAddress')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth label={t('onboarding.address')} value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder="e.g. Narva mnt 7 Tallinn"
                onKeyDown={(e) => e.key === 'Enter' && handleSearchAddress()}
              />
              <Button variant="outlined" onClick={handleSearchAddress} disabled={searching}>
                {searching ? <CircularProgress size={20} /> : t('common.search')}
              </Button>
            </Box>
            {suggestions.length > 0 && (
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                <List dense>
                  {suggestions.map((s, i) => (
                    <ListItem key={i} disablePadding>
                      <ListItemButton onClick={() => handleSelectAddress(s)} disabled={loading}>
                        <ListItemText primary={s.taisaadress || s.ipikkaadress} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="text" onClick={() => setActiveStep(1)}>{t('common.back')}</Button>
              <Button variant="text" onClick={() => setActiveStep(3)}>{t('onboarding.skipThisStep')}</Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Import Apartments */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('onboarding.importApartments')}</Typography>
            {populateResult?.apartments?.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('onboarding.foundApartments', { count: populateResult.apartments.length })}
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                  <List dense>
                    {populateResult.apartments.slice(0, 20).map((apt, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={t('onboarding.unit', { number: apt.unit_number || apt.apartment_number || i + 1 })}
                          secondary={apt.area_sqm ? `${apt.area_sqm} m²` : null}
                        />
                      </ListItem>
                    ))}
                    {populateResult.apartments.length > 20 && (
                      <ListItem>
                        <ListItemText primary={`... +${populateResult.apartments.length - 20}`} />
                      </ListItem>
                    )}
                  </List>
                </Paper>
                <Button
                  variant="contained" fullWidth onClick={handleImportApartments}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : t('onboarding.importCount', { count: populateResult.apartments.length })}
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('onboarding.noApartmentsFound')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="text" onClick={() => setActiveStep(2)}>{t('common.back')}</Button>
              <Button variant="text" onClick={() => setActiveStep(4)}>{t('common.skip')}</Button>
            </Box>
          </Box>
        )}

        {/* Step 4: Set Up Utilities */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('onboarding.setupUtilities')}</Typography>
            {utilitySuggestions.length === 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('onboarding.uploadBillDesc')}
                </Typography>
                <Button variant="outlined" component="label" sx={{ mb: 2 }}>
                  {t('onboarding.uploadBillPdf')}
                  <input
                    type="file" hidden accept=".pdf"
                    onChange={(e) => setUtilityFile(e.target.files[0])}
                  />
                </Button>
                {utilityFile && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('onboarding.selected', { filename: utilityFile.name })}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained" onClick={handleUploadBill}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : (utilityFile ? t('onboarding.analyzeBill') : t('common.skip'))}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('onboarding.foundUtilities', { count: utilitySuggestions.length })}
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto', mb: 2 }}>
                  <List dense>
                    {utilitySuggestions.map((s, i) => (
                      <ListItem key={i} disablePadding>
                        <ListItemButton onClick={() => handleToggleUtility(i)}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={selectedUtilities.includes(i)}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={s.name}
                            secondary={s.calculation_method?.replace(/_/g, ' ').toLowerCase()}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
                <Button
                  variant="contained" fullWidth onClick={handleCreateUtilities}
                  disabled={loading || selectedUtilities.length === 0}
                >
                  {loading ? <CircularProgress size={20} /> : t('onboarding.createUtilities', { count: selectedUtilities.length })}
                </Button>
                <Button variant="text" onClick={() => setActiveStep(5)} sx={{ mt: 1 }}>
                  {t('common.skip')}
                </Button>
              </>
            )}
            <Button variant="text" onClick={() => setActiveStep(3)} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>
        )}

        {/* Step 5: Done */}
        {activeStep === 5 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>{t('onboarding.allSet')}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}
              dangerouslySetInnerHTML={{ __html: t('onboarding.unionCreated', { name: createdUnion?.name }) }}
            />
            {importedCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('onboarding.apartmentsImported', { count: importedCount })}
              </Typography>
            )}
            {utilitiesCreated > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('onboarding.utilitiesCreated', { count: utilitiesCreated })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.headToDashboard')}
            </Typography>
            <Button variant="contained" size="large" onClick={handleFinish}>
              {t('onboarding.goToDashboard')}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default Onboarding;
